import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Textarea,
} from '@/components/ui'
import { useDeleteAdapter, useTestAdapter } from '@/hooks'
import { useAdapterPersonas } from '@/hooks/useAdapterPersonas'
import { useAllCredentialSets } from '@/hooks/useAllCredentialSets'
import { useAuthStore } from '@/stores/authStore'
import type { Adapter, Capability } from '@/types/toolFramework'
import type { IntegrationCatalogEntry } from '@/types/integrations'
import { ConnectFlow } from './ConnectFlow'
import { ProviderConfigModal } from './ProviderConfigModal'

interface AdapterCardProps {
  adapter: Adapter
  capabilities: Capability[]
  catalogEntry?: IntegrationCatalogEntry
  onConnectComplete?: (result: { adapterId: string; ok: boolean; error?: string }) => void
}

interface TestModalProps {
  adapter: Adapter
  open: boolean
  onClose: () => void
}

function TestModal({ adapter, open, onClose }: TestModalProps) {
  const [capId, setCapId] = useState(adapter.implements[0] ?? '')
  const [argsText, setArgsText] = useState('{}')
  const [credentialSetId, setCredentialSetId] = useState('')
  const [result, setResult] = useState<string | null>(null)
  const [errMsg, setErrMsg] = useState<string | null>(null)

  const { mutate: test, isPending } = useTestAdapter()
  const { data: credentialSets = [] } = useAllCredentialSets(open)

  function handleRun() {
    let args: Record<string, unknown>
    try {
      args = JSON.parse(argsText) as Record<string, unknown>
    } catch {
      setErrMsg('Args must be valid JSON')
      return
    }
    setResult(null)
    setErrMsg(null)
    test(
      {
        id: adapter.id,
        capability_id: capId,
        args,
        ...(credentialSetId ? { credential_set_id: credentialSetId } : {}),
      },
      {
        onSuccess(data) {
          setResult(JSON.stringify(data.result, null, 2))
          if (data.error) setErrMsg(data.error)
        },
        onError(err) {
          setErrMsg(err instanceof Error ? err.message : 'Test failed')
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <ModalContent size="sm">
        <ModalHeader>
          <ModalTitle>Test adapter: {adapter.display_name}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Capability
              </label>
              <select
                value={capId}
                onChange={(e) => setCapId(e.target.value)}
                className="w-full text-sm rounded-md border border-border bg-background px-3 py-1.5 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
              >
                {adapter.implements.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Credential set
              </label>
              <select
                value={credentialSetId}
                onChange={(e) => setCredentialSetId(e.target.value)}
                data-testid="credential-set-select"
                className="w-full text-sm rounded-md border border-border bg-background px-3 py-1.5 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
              >
                <option value="">None (no credentials)</option>
                {credentialSets.map((cs) => (
                  <option key={cs.id} value={cs.id}>
                    {cs.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Required for adapters that need credentials (e.g. Lalamove, Stripe).
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Args (JSON)
              </label>
              <Textarea
                value={argsText}
                onChange={(e) => setArgsText(e.target.value)}
                rows={4}
                className="font-mono text-xs"
                data-testid="args-input"
              />
            </div>
            {errMsg && (
              <Alert variant="destructive">
                <AlertDescription>{errMsg}</AlertDescription>
              </Alert>
            )}
            {result !== null && (
              <pre className="rounded-sm bg-muted p-2 text-xs font-mono overflow-auto max-h-40">
                {result}
              </pre>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button size="sm" onClick={handleRun} isLoading={isPending} data-testid="run-btn">
            Run
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export function AdapterCard({ adapter, capabilities, catalogEntry, onConnectComplete }: AdapterCardProps) {
  const [testOpen, setTestOpen] = useState(false)
  const [configureOpen, setConfigureOpen] = useState(false)
  const isMcp = adapter.kind === 'mcp'
  const isOauth = catalogEntry?.auth_kind === 'oauth2'
  const configured = catalogEntry?.configured ?? false

  const conn = catalogEntry?.connection
  const statusBadge = isOauth ? (
    conn?.status === 'connected' ? (
      <Badge variant="success">
        Connected{conn.expires_at ? ` · expires ${new Date(conn.expires_at).toLocaleTimeString()}` : ''}
      </Badge>
    ) : conn?.status === 'expired' ? (
      <Badge variant="warning">Reconnect needed</Badge>
    ) : conn?.status === 'error' ? (
      <Badge variant="destructive">Error</Badge>
    ) : configured ? (
      <Badge variant="secondary">Configured</Badge>
    ) : (
      <Badge variant="outline">Not configured</Badge>
    )
  ) : null

  const { mutate: remove } = useDeleteAdapter()

  function handleDisconnect() {
    if (!window.confirm(`Disconnect adapter "${adapter.display_name}"?`)) return
    remove(adapter.id)
  }

  function capLabel(capId: string) {
    const cap = capabilities.find((c) => c.id === capId)
    return cap ? `${capId} (${cap.category})` : capId
  }

  return (
    <>
      <Card className="flex flex-col gap-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-foreground">
              {adapter.display_name}
            </p>
            <p className="font-mono text-xs text-muted-foreground">{adapter.id}</p>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge}
            <Badge variant={isMcp ? 'secondary' : 'outline'}>
              {isMcp ? 'MCP' : 'Native'}
            </Badge>
          </div>
        </div>

        {adapter.implements.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
              Implements:
            </p>
            <div className="flex flex-wrap gap-1">
              {adapter.implements.map((capId) => (
                <span
                  key={capId}
                  className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-foreground font-mono"
                >
                  {capLabel(capId)}
                </span>
              ))}
            </div>
          </div>
        )}

        <AdapterStatusPanel adapterId={adapter.id} />

        <div className="flex items-center gap-2 pt-1 border-t border-border mt-auto">
          {isOauth && (
            <Button variant="secondary" size="sm" onClick={() => setConfigureOpen(true)}>
              Configure
            </Button>
          )}
          {isMcp && (
            isOauth && !configured ? (
              <Button size="sm" disabled title="Configure the OAuth client first">
                Connect
              </Button>
            ) : (
              <ConnectFlow
                adapterId={adapter.id}
                onComplete={onConnectComplete ?? (() => {})}
              >
                <Button size="sm" variant="default">
                  Connect
                </Button>
              </ConnectFlow>
            )
          )}
          <Button size="sm" variant="secondary" onClick={() => setTestOpen(true)}>
            Test
          </Button>
          {isMcp && (
            <Button size="sm" variant="destructive" onClick={handleDisconnect}>
              Disconnect
            </Button>
          )}
        </div>
      </Card>

      <TestModal adapter={adapter} open={testOpen} onClose={() => setTestOpen(false)} />
      {isOauth && catalogEntry && (
        <ProviderConfigModal
          adapterId={adapter.id}
          catalogEntry={catalogEntry}
          open={configureOpen}
          onClose={() => setConfigureOpen(false)}
        />
      )}
    </>
  )
}

/**
 * Admin-only diagnostic panel: lists every persona that binds the adapter,
 * along with the credential set wired to each binding (or a warning marker
 * when none is set). Wiring itself happens on the persona's bundle editor —
 * each row links to that editor. Hidden for non-admins (returns null).
 */
function AdapterStatusPanel({ adapterId }: { adapterId: string }) {
  const isAdmin = useAuthStore((s) => s.user?.roles?.includes('admin') ?? false)
  const { data: rows = [], isLoading } = useAdapterPersonas(
    isAdmin ? adapterId : '',
  )

  if (!isAdmin) return null
  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground">Loading persona bindings…</p>
    )
  }

  return (
    <div className="border-t border-border pt-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Used by personas:
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No personas bind this adapter yet.
        </p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li
              key={`${r.persona_id}-${r.capability_id}`}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span aria-hidden>{r.source === 'none' ? '⚠' : '✓'}</span>
                <span className="font-medium">{r.persona_name}</span>
                <span className="font-mono text-muted-foreground">{r.capability_id}</span>
                <span className="text-muted-foreground">
                  {r.source === 'explicit'
                    ? (r.effective_credential_set_name ?? r.credential_set_name ?? '(set)')
                    : r.source === 'deployment'
                      ? `deployment connection${r.effective_credential_set_name ? ` (${r.effective_credential_set_name})` : ''}`
                      : '(no credential set)'}
                </span>
              </div>
              <Link
                to={`/personas/${r.persona_id}/workshop`}
                className="text-primary hover:underline shrink-0"
              >
                Edit →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
